import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Trans } from '@common/i18n/trans';
import { useFormContext } from 'react-hook-form';
import { FormSelect } from '@common/ui/forms/select/select';
import { Item } from '@common/ui/forms/listbox/item';
import { Button } from '@common/ui/buttons/button';
import { useTrans } from '@common/i18n/use-trans';
import { DeleteIcon } from '@common/icons/material/Delete';
import { AnalyzeAction } from './analyze-action';
import { useRemoveAnalyzeSubItemRateSheet } from '@app_modules/requests/use-remove-analyze-sub-item-rate-sheet';
import { DetailInfoSuggested } from '../analysis-detail-info-suggested';
import moment from 'moment-timezone';
import { useUserTimezone } from '@common/i18n/use-user-timezone';

// Type definitions
interface SubItem {
  analyzed_at?: string;
  result_execution?: { result: any[] | any } | any[];
  result?: any[] | any;
  investor_code?: string;
  sheet_name?: string;
}

interface UploadedFileItem {
  report_id: string;
  status: string;
  investor_code: string;
}

interface WebSocketEventData {
  [key: string]: {
    analyzed_at?: string;
    resultExecution?: { result: any[] | any } | any[];
  };
}

interface RowSheetProps {
  id: number;
  market_rate_date: string;
  loan_program_type: string;
  description: string,
  analysis_prompt: string,
  item: unknown; // Replace with specific type if known
  subItem: SubItem;
  pidx: number;
  index: number;
  subItems: SubItem[];
  onAddSubItems: (pidx: number, subItems: SubItem[]) => void;
  setCnt: (count: number) => void;
  cnt: number;
  fetchInvestorCode: (id: number, loan_program_type: string) => Promise<{ data: { investor_codes: string[] } }>;
  fetchSheetName: (id: number, investor_code: string, loan_program_type: string) => Promise<{ data: { sheet_names: string[] } }>;
}

export function RowSheet({
  id,
  market_rate_date,
  loan_program_type,
  description,
  analysis_prompt,
  item,
  subItem,
  pidx,
  index,
  subItems,
  onAddSubItems,
  setCnt,
  cnt,
  fetchInvestorCode,
  fetchSheetName,
}: RowSheetProps) {
  const { trans } = useTrans();
  const timezone = useUserTimezone();
  const { watch, setValue } = useFormContext();

  // Watch form fields
  const investor_code = watch(`${pidx}_${index}_investor_code`);
  const sheet_name = watch(`${pidx}_${index}_sheet_name`);
  // const description = watch(`${pidx}_${index}_description`);
  // const analysis_prompt = watch(`${pidx}_${index}_analysis_prompt`);

  // State for dropdown options and results
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [investorCodes, setInvestorCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resultExecution, setResultExecution] = useState<any[]>([]);
  const [analyzedAt, setAnalyzedAt] = useState(subItem.analyzed_at || '');

  // Check if the investor_code and sheet_name combination already exists in another sub-item
  const isDuplicateCombination = (newInvestorCode: string, newSheetName: string, currentIndex: number) => {
    return subItems.some((item, idx) => {
      if (idx === currentIndex) return false; // Skip the current sub-item
      return item.investor_code === newInvestorCode && item.sheet_name === newSheetName;
    });
  };

  // Fetch investor codes when loan_program_type changes
  useEffect(() => {
    if (!loan_program_type) return;
    setIsLoading(true);
    fetchInvestorCode(id, loan_program_type)
      .then((response) => {
        setInvestorCodes(response.data.investor_codes || []);
      })
      .catch((error) => {
        console.error('Failed to fetch investor codes:', error);
        setInvestorCodes([]);
      })
      .finally(() => setIsLoading(false));
  }, [id, loan_program_type, fetchInvestorCode]);

  // Fetch sheet names when investor_code and loan_program_type change
  useEffect(() => {
    if (!investor_code || !loan_program_type) return;
    setIsLoading(true);
    fetchSheetName(id, investor_code, loan_program_type)
      .then((response) => {
        setSheetNames(response.data.sheet_names || []);
      })
      .catch((error) => {
        console.error('Failed to fetch sheet names:', error);
        setSheetNames([]);
      })
      .finally(() => setIsLoading(false));
  }, [id, investor_code, loan_program_type, fetchSheetName]);

  // Compute resultExecution from subItem
  useEffect(() => {
    let newResultExecution: any[] = [];

    if (subItem?.result_execution) {
      if ('result' in subItem.result_execution) {
        const result = subItem.result_execution.result;
        if (Array.isArray(result)) {
          newResultExecution = result;
        } else if (result !== undefined && result !== null) {
          newResultExecution = [result];
        }
      } else {
        newResultExecution = Array.isArray(subItem.result_execution)
          ? subItem.result_execution
          : [subItem.result_execution];
      }
    } else if (subItem?.result) {
      newResultExecution = Array.isArray(subItem.result) ? subItem.result : [subItem.result];
    }

    setResultExecution(newResultExecution);
  }, [subItem?.result_execution, subItem?.result]);

  // WebSocket listener for real-time updates
  useEffect(() => {
    if (!id || !loan_program_type || !investor_code || !sheet_name) return;

    const channel = window.Echo.channel('rate-sheet');
    channel.listen('.analyze', (e: { data: WebSocketEventData }) => {
      const key = `${id}-${loan_program_type}-${investor_code}-${sheet_name}`;
      const item = e.data?.[key];

      if (!item) {
        console.warn(`No item found for key: ${key}`, e.data);
        return;
      }

      if (item.resultExecution) {
        if ('result' in item.resultExecution) {
          const result = item.resultExecution.result;
          if (Array.isArray(result)) {
            setResultExecution(result);
          } else if (result !== undefined && result !== null) {
            setResultExecution([result]);
          }
        } else {
          setResultExecution(Array.isArray(item.resultExecution) ? item.resultExecution : [item.resultExecution]);
        }
      }

      if (item.analyzed_at && item.analyzed_at !== analyzedAt) {
        try {
          new Date(item.analyzed_at);
          setAnalyzedAt(item.analyzed_at);
        } catch (error) {
          console.error('Invalid analyzed_at format:', item.analyzed_at, error);
        }
      }
    });

    return () => {
      channel.stopListening('.analyze');
    };
  }, [id, loan_program_type, investor_code, sheet_name, analyzedAt]);

  // Handle delete action
  const removeAnalyzeSubItemRateSheet = useRemoveAnalyzeSubItemRateSheet(
    id,
    loan_program_type,
    investor_code,
    sheet_name
  );

  const [executeAnalyzeAction, setExecuteAnalyzeAction] = useState<any>();
  const onProcess = (action: any) => {
    // TODO: Implement status update logic if needed
    setExecuteAnalyzeAction(action);
  };

  return (
    <tr
      key={`meta-${pidx}-${index}-${id}`}
      className="border-b transition duration-300 ease-in-out hover:bg-gray-100 w-[100%]"
    >
      <td className="pl-40 w-[10%] text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap border-r">
        <FormSelect
          name={`${pidx}_${index}_investor_code`}
          selectionMode="single"
          required
          placeholder={trans({ message: 'Select Lender' })}
          className="w-[170px]"
          disabled={isLoading}
          onSelectionChange={(val) => {
            const newInvestorCode = val.toString();
            if (sheet_name && isDuplicateCombination(newInvestorCode, sheet_name, index)) {
              console.warn(`The combination of Lender "${newInvestorCode}" and Sheet "${sheet_name}" already exists.`);
              return;
            }
            const updatedSubItems = [...subItems];
            updatedSubItems[index].investor_code = newInvestorCode;
            onAddSubItems(pidx, updatedSubItems);
            setValue(`${pidx}_${index}_investor_code`, newInvestorCode);
          }}
        >
          {investorCodes.map((code) => (
            <Item value={code} key={code}>
              <Trans message={code} />
            </Item>
          ))}
        </FormSelect>
      </td>
      <td className="w-[10%] text-sm text-gray-900 px-6 py-4 whitespace-nowrap border-r border-l">
        <FormSelect
          name={`${pidx}_${index}_sheet_name`}
          selectionMode="single"
          required
          placeholder={trans({ message: 'Select sheet name' })}
          className="w-[170px]"
          disabled={isLoading}
          onSelectionChange={(val) => {
            const newSheetName = val.toString();
            if (investor_code && isDuplicateCombination(investor_code, newSheetName, index)) {
              console.warn(`The combination of Lender "${investor_code}" and Sheet "${newSheetName}" already exists.`);
              return;
            }
            const updatedSubItems = [...subItems];
            updatedSubItems[index].sheet_name = newSheetName;
            onAddSubItems(pidx, updatedSubItems);
            setValue(`${pidx}_${index}_sheet_name`, newSheetName);
          }}
        >
          {sheetNames.map((name) => (
            <Item value={name} key={name}>
              <Trans message={name} />
            </Item>
          ))}
        </FormSelect>
      </td>
      <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap border-r">
        <AnalyzeAction
          id={id}
          market_rate_date={market_rate_date}
          onProcess={onProcess}
          key={`action-${pidx}-${index}`}
          subCheckList={subItem}
          pindex={pidx}
          investor_code={investor_code}
          loan_program_type={loan_program_type}
          sheet_name={sheet_name}
          description={description}
          analysis_prompt={analysis_prompt}
        />
      </td>
      <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap border-r">
        {executeAnalyzeAction && executeAnalyzeAction.isPending ? 'Processing': (analyzedAt
          ? moment.utc(analyzedAt).tz(timezone).format('MM/DD/YYYY HH:mm:ss')
          : '')}
      </td>
      <td
        className="w-[15%] text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap border-r"
        colSpan={2}
      >
        {executeAnalyzeAction && executeAnalyzeAction.isPending ? (<>
        <span className="text-gray-500">No results</span>
        </>):(<>
          {resultExecution.length > 0 ? (
          <DetailInfoSuggested items={resultExecution} />
        ) : (
          <span className="text-gray-500">No results</span>
        )}
        </>)}
        
      </td>
      <td className="w-[5%] text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap border-r">
        {/* <Button
          size="xs"
          variant="outline"
          color="orange"
          onClick={handleDelete}
          disabled={removeAnalyzeSubItemRateSheet.isPending || subItems.length <= 1}
        >
          <DeleteIcon />
        </Button> */}
        {subItems.length>1?(
        <Button
            size={'xs'} variant="outline" color="orange"
            onClick={() => {
              if (subItems.length > 1) {
                subItems.splice(index, 1);
              }
              if (cnt > 1) {
                setCnt(cnt-1);
              } else {
                setCnt(1);
              }
              onAddSubItems(pidx, subItems);

              if (id && investor_code && loan_program_type) {
                removeAnalyzeSubItemRateSheet.mutate(
                  {id: id, loan_program_type: loan_program_type, investor_code: investor_code, sheet_name: sheet_name},
                  {
                    onSuccess: (response) => {
                      //onProcess(response.status, response.total_files, response.uploaded_date, response.file_name);
                    },
              
                  },
                );
              }
            }}
            disabled={removeAnalyzeSubItemRateSheet.isPending}
            >
            <DeleteIcon />
          </Button>):(<></>)}
      </td>
    </tr>
  );
}